import { useEffect, useRef, useSyncExternalStore, type RefObject } from "react";
import type { EnvironmentId } from "@cmmd-forge/contracts";

import { refreshCloudRuntimeStatus } from "../components/sidebar/connectCloudRuntime";
import {
  readEnvironmentConnection,
  subscribeEnvironmentConnections,
} from "../environments/runtime";

const RUNTIME_STATUS_REFRESH_TTL_MS = 30_000;

export function shouldRefreshRuntimeStatus(input: {
  readonly lastRefreshedAt: number | undefined;
  readonly now: number;
  readonly ttlMs?: number;
}): boolean {
  if (input.lastRefreshedAt === undefined) return true;
  return input.now - input.lastRefreshedAt >= (input.ttlMs ?? RUNTIME_STATUS_REFRESH_TTL_MS);
}

export function useIsCloudEnvironmentConnected(environmentId: string | null | undefined): boolean {
  return useSyncExternalStore(
    subscribeEnvironmentConnections,
    () =>
      environmentId ? readEnvironmentConnection(environmentId as EnvironmentId) !== null : true,
    () => true,
  );
}

function refreshRuntimeStatusWhenStale(
  runtimeId: string,
  refreshedRuntimeIdsAt: RefObject<Map<string, number>>,
): void {
  const now = Date.now();
  if (
    !shouldRefreshRuntimeStatus({
      lastRefreshedAt: refreshedRuntimeIdsAt.current.get(runtimeId),
      now,
    })
  ) {
    return;
  }
  refreshedRuntimeIdsAt.current.set(runtimeId, now);
  void refreshCloudRuntimeStatus(runtimeId);
}

export function useRefreshCloudRuntimeStatus(runtimeId: string | null): void {
  const refreshedRuntimeIdsAt = useRef<Map<string, number>>(new Map());
  useEffect(() => {
    if (!runtimeId) return;
    const refresh = (): void => refreshRuntimeStatusWhenStale(runtimeId, refreshedRuntimeIdsAt);
    refresh();
    const onVisible = (): void => {
      if (document.visibilityState === "visible") refresh();
    };
    document.addEventListener("visibilitychange", onVisible);
    return () => document.removeEventListener("visibilitychange", onVisible);
  }, [runtimeId]);
}
